import { useCallback, useEffect, useState } from 'react';
import { Company, CompanyPageCursor, Network, companiesService, networksService } from '../services/firebaseFunctions';

const detailPageSize = 25;

const getErrorMessage = (error: unknown, fallback: string) => (
  error instanceof Error ? error.message : fallback
);

const isConflict = (error: unknown) => (
  (error as { code?: string }).code === 'aborted'
  || (error instanceof Error && error.message.includes('aborted'))
);

export const useNetworkDetail = (networkId?: string) => {
  const [network, setNetwork] = useState<Network | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [eligibleCompanies, setEligibleCompanies] = useState<Company[]>([]);
  const [eligibleNextCursor, setEligibleNextCursor] = useState<CompanyPageCursor | null>(null);
  const [nextCursor, setNextCursor] = useState<CompanyPageCursor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (cursor?: CompanyPageCursor) => {
    if (!networkId) return;

    try {
      setLoading(true);
      setError(null);
      const [networkItem, companiesPage, eligiblePage] = await Promise.all([
        networksService.get(networkId),
        companiesService.listPage({ networkId, pageSize: detailPageSize, cursor }),
        companiesService.listPage({ pageSize: detailPageSize }),
      ]);

      setNetwork(networkItem);
      setCompanies((current) => cursor ? [...current, ...companiesPage.items] : companiesPage.items);
      setEligibleCompanies(eligiblePage.items.filter((company) => company.networkId !== networkId));
      setEligibleNextCursor(eligiblePage.nextCursor);
      setNextCursor(companiesPage.nextCursor);
    } catch (caught) {
      setError(getErrorMessage(caught, 'Erro ao carregar rede'));
    } finally {
      setLoading(false);
    }
  }, [networkId]);

  useEffect(() => {
    void load();
  }, [load]);

  const setCompanyNetwork = async (company: Company, target: string | null) => {
    try {
      await companiesService.setNetwork(company.id, target, company.networkId || null);
    } catch (caught) {
      if (isConflict(caught)) await load();
      throw caught;
    }

    await load();
  };

  const searchEligible = async (search: string, cursor?: CompanyPageCursor) => {
    try {
      const result = await companiesService.listPage({
        pageSize: detailPageSize,
        search: search.trim() || undefined,
        cursor,
      });
      const items = result.items.filter((company) => company.networkId !== networkId);
      setEligibleCompanies((current) => cursor ? [...current, ...items] : items);
      setEligibleNextCursor(result.nextCursor);
    } catch (caught) {
      setError(getErrorMessage(caught, 'Erro ao buscar empresas elegíveis'));
    }
  };

  return {
    network,
    companies,
    eligibleCompanies,
    eligibleNextCursor,
    nextCursor,
    loading,
    error,
    refresh: () => load(),
    loadMore: () => nextCursor && load(nextCursor),
    searchEligible,
    loadMoreEligible: (search: string) => eligibleNextCursor && searchEligible(search, eligibleNextCursor),
    setCompanyNetwork,
  };
};
